import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

export type WorkspaceRole = 'Owner' | 'Manager' | 'Viewer';
export interface Workspace { id: string; name: string; role: WorkspaceRole }
interface Access { userId: string; workspaceId: string; role: WorkspaceRole; workspaces: Workspace[] }
interface State { access: Access | null; error: boolean; canManage: boolean; isOwner: boolean; refresh: () => Promise<Access | null>; select: (id: string) => void }
const Context = createContext<State | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, tenantId } = useAuth();
  const [access, setAccess] = useState<Access | null>(null);
  const latest = useRef<Access | null>(null);
  const [error, setError] = useState(false);
  const request = useRef<AbortController | null>(null);
  const refresh = useCallback(async () => {
    request.current?.abort(); const controller = new AbortController(); request.current = controller;
    try {
      const { data } = await api.get<Access>('/agency/access', { signal: controller.signal, timeout: 15000 });
      if (!Array.isArray(data?.workspaces) || !['Owner', 'Manager', 'Viewer'].includes(data.role)) throw new Error('Invalid workspace response');
      if (controller.signal.aborted) return null;
      sessionStorage.setItem('activeWorkspaceId', data.workspaceId);
      latest.current = data; setAccess(data); setError(false); return data;
    } catch { if (!controller.signal.aborted) setError(true); return null; }
  }, []);
  useEffect(() => {
    if (!isAuthenticated) { latest.current = null; setAccess(null); setError(false); return; }
    void refresh();
    const focus = () => { if (!document.hidden) void refresh(); };
    window.addEventListener('focus', focus); const timer = setInterval(focus, 60000);
    return () => { request.current?.abort(); clearInterval(timer); window.removeEventListener('focus', focus); };
  }, [isAuthenticated, tenantId, refresh]);
  const select = (id: string) => {
    if (!latest.current?.workspaces.some(w => w.id === id)) return;
    sessionStorage.setItem('activeWorkspaceId', id);
    // Clear tenant data, caches and old realtime connections when changing workspace.
    window.location.assign('/dashboard/agency');
  };
  return <Context.Provider value={{ access, error, canManage: access?.role === 'Owner' || access?.role === 'Manager', isOwner: access?.role === 'Owner', refresh, select }}>{children}</Context.Provider>;
}
export function useWorkspace() { const value = useContext(Context); if (!value) throw new Error('WorkspaceProvider is required'); return value; }
